import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { apiClient } from '../../../api/client';
import { ZoomPanCanvas } from './ZoomPanCanvas';
import { TableCircle, TableData } from './TableCircle';
import { SeatData } from './SeatDot';
import { FloorplanTooltip } from './FloorplanTooltip';
import { FloorplanSearch } from './FloorplanSearch';
import { ZoomControls } from './ZoomControls';
import { ZONE_COLORS, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, FONT_HEADING } from './designTokens';
import { FloorplanGridView } from './FloorplanGridView';

interface FloorplanViewProps {
  onSelectTable: (tableId: string) => void;
  refreshKey: number;
}

interface ZoneData {
  id: string;
  name: string;
  colorCode: string;
  sortOrder: number;
}

interface TableDetailResponse extends TableData {
  seats: SeatData[];
}

// --- Grand Ballroom Layout Constants ---
// Layout: Grand Entrance at top → main walkway down center → dance floor/stage at bottom
// Main grid: 6 columns x 7 rows on EACH side of the walkway = up to 84 positions
// Plus 2 "flanking" tables on each side of the dance floor = 4 extra positions
// Total capacity: ~80 positions (row-set 1 has no aisle tables — dance floor area)
const LEFT_COLS = 6;
const RIGHT_COLS = 6;
const TABLE_ROWS = 7;
const TABLE_SPACING_X = 110;
const TABLE_SPACING_Y = 110;
const WALKWAY_WIDTH = 120; // main walkway from grand entrance to dance floor
const GRID_OFFSET_X = 140; // left margin for salon labels
const GRID_OFFSET_Y = 160; // top margin below entrance header

// Dance floor flanking tables (2 on each side of the dance floor)
const FLANK_TABLES = 2; // per side

// Total SVG dimensions
const GRID_WIDTH = LEFT_COLS * TABLE_SPACING_X + WALKWAY_WIDTH + RIGHT_COLS * TABLE_SPACING_X;
const TOTAL_WIDTH = GRID_OFFSET_X + GRID_WIDTH + 140;
const TOTAL_HEIGHT = GRID_OFFSET_Y + TABLE_ROWS * TABLE_SPACING_Y + 300; // extra for dance floor + stage

// Salon labels positioned around the perimeter
const SALON_LABELS = [
  { label: 'Salon A', x: 40, y: GRID_OFFSET_Y + 80, rotate: -90 },
  { label: 'Salon B', x: 40, y: GRID_OFFSET_Y + 280, rotate: -90 },
  { label: 'Salon C', x: 40, y: GRID_OFFSET_Y + 480, rotate: -90 },
  { label: 'Salon D', x: TOTAL_WIDTH - 40, y: GRID_OFFSET_Y + 80, rotate: 90 },
  { label: 'Salon E', x: TOTAL_WIDTH - 40, y: GRID_OFFSET_Y + 280, rotate: 90 },
  { label: 'Salon F', x: TOTAL_WIDTH - 40, y: GRID_OFFSET_Y + 480, rotate: 90 },
  { label: 'Grand Entrance', x: TOTAL_WIDTH / 2, y: 40, rotate: 0 },
  { label: 'Foyer / Pre-Function', x: TOTAL_WIDTH / 2, y: 62, rotate: 0 },
];

/**
 * Compute table positions in the Grand Ballroom layout:
 * - Grand Entrance at top, main walkway runs vertically down center
 * - Left block: 6 cols x 7 rows, Right block: 6 cols x 7 rows
 * - Below the main grid: 2 flanking tables on each side of the dance floor
 * - Stage envelops the dance floor at the very bottom
 */
function computeTablePositions(
  tables: TableData[],
  zones: ZoneData[],
): {
  positions: Map<string, { x: number; y: number }>;
  zoneLabels: { name: string; y: number; color: string }[];
  totalHeight: number;
} {
  const positions = new Map<string, { x: number; y: number }>();
  const zoneLabels: { name: string; y: number; color: string }[] = [];

  // Sort all tables by zone order, then table number
  const sortedTables: TableData[] = [];
  for (const zone of zones) {
    const zoneTables = tables
      .filter((t) => t.zone.name === zone.name)
      .sort((a, b) => a.tableNumber - b.tableNumber);
    if (zoneTables.length > 0) {
      sortedTables.push(...zoneTables);
    }
  }

  // Main grid capacity
  const mainGridCapacity = (LEFT_COLS + RIGHT_COLS) * TABLE_ROWS; // 84
  const flankCapacity = FLANK_TABLES * 2; // 4
  const totalCols = LEFT_COLS + RIGHT_COLS;
  let currentZoneIndex = 0;

  for (let i = 0; i < sortedTables.length; i++) {
    const table = sortedTables[i];
    let x: number;
    let y: number;

    if (i < mainGridCapacity) {
      // Main grid placement: fill left-to-right across both blocks, row by row
      const row = Math.floor(i / totalCols);
      const col = i % totalCols;

      if (col < LEFT_COLS) {
        // Left block
        x = GRID_OFFSET_X + col * TABLE_SPACING_X;
      } else {
        // Right block (after walkway gap)
        x = GRID_OFFSET_X + LEFT_COLS * TABLE_SPACING_X + WALKWAY_WIDTH + (col - LEFT_COLS) * TABLE_SPACING_X;
      }
      y = GRID_OFFSET_Y + row * TABLE_SPACING_Y;
    } else if (i < mainGridCapacity + flankCapacity) {
      // Flanking tables beside the dance floor (below the main grid)
      const flankIndex = i - mainGridCapacity;
      const danceFloorY = GRID_OFFSET_Y + TABLE_ROWS * TABLE_SPACING_Y + 40;
      const centerX = GRID_OFFSET_X + LEFT_COLS * TABLE_SPACING_X + WALKWAY_WIDTH / 2;

      if (flankIndex < FLANK_TABLES) {
        // Left side of dance floor
        x = centerX - 200 - flankIndex * TABLE_SPACING_X;
        y = danceFloorY + flankIndex * 70;
      } else {
        // Right side of dance floor
        const rIdx = flankIndex - FLANK_TABLES;
        x = centerX + 200 + rIdx * TABLE_SPACING_X;
        y = danceFloorY + rIdx * 70;
      }
    } else {
      // Overflow: stack below stage area
      const overflowIdx = i - mainGridCapacity - flankCapacity;
      const overflowRow = Math.floor(overflowIdx / totalCols);
      const overflowCol = overflowIdx % totalCols;
      x = GRID_OFFSET_X + overflowCol * TABLE_SPACING_X;
      y = TOTAL_HEIGHT - 60 + overflowRow * TABLE_SPACING_Y;
    }

    positions.set(table.id, { x, y });

    // Track zone transitions for labels
    const zoneIdx = zones.findIndex((z) => z.name === table.zone.name);
    if (zoneIdx !== currentZoneIndex && i < mainGridCapacity) {
      currentZoneIndex = zoneIdx;
      const zone = zones[zoneIdx];
      if (zone) {
        zoneLabels.push({
          name: zone.name,
          y: y - 20,
          color: ZONE_COLORS[zone.name] || zone.colorCode,
        });
      }
    }
  }

  // Add initial zone label
  if (zones.length > 0 && sortedTables.length > 0) {
    const firstZone = zones.find((z) => sortedTables[0]?.zone.name === z.name);
    if (firstZone && !zoneLabels.find((zl) => zl.name === firstZone.name)) {
      zoneLabels.unshift({
        name: firstZone.name,
        y: GRID_OFFSET_Y - 20,
        color: ZONE_COLORS[firstZone.name] || firstZone.colorCode,
      });
    }
  }

  return { positions, zoneLabels, totalHeight: TOTAL_HEIGHT };
}

export function FloorplanView({ onSelectTable, refreshKey }: FloorplanViewProps) {
  const [floorplanMode, setFloorplanMode] = useState<'grid' | 'classic'>('grid');

  return (
    <div>
      {/* View mode toggle */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', overflow: 'hidden' }}>
          <button
            style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', border: 'none', cursor: 'pointer', backgroundColor: floorplanMode === 'grid' ? '#1a6b3c' : '#f5f5f5', color: floorplanMode === 'grid' ? '#fff' : '#6b7280', fontWeight: 600 }}
            onClick={() => setFloorplanMode('grid')}
            aria-label="Grid view"
          >
            🏛️ Grid
          </button>
          <button
            style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', border: 'none', borderLeft: `1px solid ${BORDER_LIGHT}`, cursor: 'pointer', backgroundColor: floorplanMode === 'classic' ? '#1a6b3c' : '#f5f5f5', color: floorplanMode === 'classic' ? '#fff' : '#6b7280', fontWeight: 600 }}
            onClick={() => setFloorplanMode('classic')}
            aria-label="Classic view"
          >
            🗺️ Classic
          </button>
        </div>
      </div>

      {floorplanMode === 'grid' && (
        <FloorplanGridView onSelectTable={onSelectTable} refreshKey={refreshKey} />
      )}
      {floorplanMode === 'classic' && (
        <ClassicFloorplanView onSelectTable={onSelectTable} refreshKey={refreshKey} />
      )}
    </div>
  );
}

function ClassicFloorplanView({ onSelectTable, refreshKey }: FloorplanViewProps) {
  const [tables, setTables] = useState<TableData[]>([]);
  const [zones, setZones] = useState<ZoneData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Zoom/pan state
  const [scale, setScale] = useState(1.0);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Tooltip state
  const [tooltipTable, setTooltipTable] = useState<TableData | null>(null);
  const [tooltipSeats, setTooltipSeats] = useState<SeatData[]>([]);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seat detail cache
  const seatCacheRef = useRef<Map<string, SeatData[]>>(new Map());

  // Keyboard navigation
  const tableRefs = useRef<Map<string, SVGGElement>>(new Map());
  const [, setFocusedTableId] = useState<string | null>(null);

  // Responsive
  const [isSmallViewport, setIsSmallViewport] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [zonesData, tablesData] = await Promise.all([
        apiClient<ZoneData[]>('/table-management/zones'),
        apiClient<TableData[]>('/table-management/tables'),
      ]);
      setZones(zonesData.sort((a, b) => a.sortOrder - b.sortOrder));
      setTables(tablesData);
    } catch {
      setError('Failed to load table data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (refreshKey > 0) {
      seatCacheRef.current.clear();
      fetchData();
    }
  }, [refreshKey, fetchData]);

  // Responsive check
  useEffect(() => {
    const checkViewport = () => {
      setIsSmallViewport(window.innerWidth < 768);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Compute positions
  const { positions, zoneLabels, totalHeight } = useMemo(
    () => computeTablePositions(tables, zones),
    [tables, zones],
  );

  const totalWidth = useMemo(() => TOTAL_WIDTH, []);

  // Search logic
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return { matchedTableIds: new Set<string>(), matchedSeatIds: new Set<string>() };

    const q = searchQuery.toLowerCase().trim();
    const matchedTableIds = new Set<string>();
    const matchedSeatIds = new Set<string>();

    for (const table of tables) {
      const tableNumStr = table.tableNumber.toString();
      const nameMatch = table.tableName.toLowerCase().includes(q);
      const numMatch = tableNumStr.includes(q);

      if (nameMatch || numMatch) {
        matchedTableIds.add(table.id);
        continue;
      }

      const cachedSeats = seatCacheRef.current.get(table.id);
      if (cachedSeats) {
        for (const seat of cachedSeats) {
          if (seat.guestName && seat.guestName.toLowerCase().includes(q)) {
            matchedTableIds.add(table.id);
            matchedSeatIds.add(seat.id);
          }
        }
      }
    }

    return { matchedTableIds, matchedSeatIds };
  }, [searchQuery, tables]);

  // Zoom handlers
  const handleZoom = useCallback((delta: number, _centerX: number, _centerY: number) => {
    setScale((prev) => Math.min(2.0, Math.max(0.4, prev + delta)));
  }, []);

  const handlePan = useCallback((dx: number, dy: number) => {
    setTranslateX((prev) => prev + dx / scale);
    setTranslateY((prev) => prev + dy / scale);
  }, [scale]);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(2.0, prev + 0.1));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(0.4, prev - 0.1));
  }, []);

  const handleResetFit = useCallback(() => {
    if (!containerRef.current) {
      setScale(0.85);
      setTranslateX(0);
      setTranslateY(0);
      return;
    }
    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;
    const scaleX = containerWidth / totalWidth;
    const scaleY = containerHeight / totalHeight;
    const fitScale = Math.min(Math.max(0.4, Math.min(scaleX, scaleY, 2.0)), 2.0);
    setScale(fitScale);
    setTranslateX(0);
    setTranslateY(0);
  }, [totalWidth, totalHeight]);

  // Tooltip handlers
  const fetchSeatDetails = useCallback(async (tableId: string): Promise<SeatData[]> => {
    const cached = seatCacheRef.current.get(tableId);
    if (cached) return cached;

    try {
      const detail = await apiClient<TableDetailResponse>(`/table-management/tables/${tableId}`);
      const seats = detail.seats || [];
      seatCacheRef.current.set(tableId, seats);
      return seats;
    } catch {
      return [];
    }
  }, []);

  const handleTableMouseEnter = useCallback(
    (table: TableData, event: React.MouseEvent) => {
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
      setTooltipPosition({ x: event.clientX, y: event.clientY });
      tooltipTimerRef.current = setTimeout(async () => {
        setTooltipTable(table);
        const seats = await fetchSeatDetails(table.id);
        setTooltipSeats(seats);
        setTooltipVisible(true);
      }, 200);
    },
    [fetchSeatDetails],
  );

  const handleTableMouseLeave = useCallback(() => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    tooltipTimerRef.current = setTimeout(() => {
      setTooltipVisible(false);
      setTooltipTable(null);
      setTooltipSeats([]);
    }, 150);
  }, []);

  // Keyboard navigation
  const orderedTableIds = useMemo(() => {
    const ids: string[] = [];
    for (const zone of zones) {
      const zoneTables = tables
        .filter((t) => t.zone.name === zone.name)
        .sort((a, b) => a.tableNumber - b.tableNumber);
      for (const t of zoneTables) {
        ids.push(t.id);
      }
    }
    return ids;
  }, [tables, zones]);

  const GRID_TOTAL_COLS = LEFT_COLS + RIGHT_COLS;

  const handleTableKeyDown = useCallback(
    (tableId: string, event: React.KeyboardEvent) => {
      const currentIndex = orderedTableIds.indexOf(tableId);
      if (currentIndex === -1) return;

      let nextIndex = -1;

      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          nextIndex = Math.min(currentIndex + 1, orderedTableIds.length - 1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          nextIndex = Math.max(currentIndex - 1, 0);
          break;
        case 'ArrowDown':
          event.preventDefault();
          nextIndex = Math.min(currentIndex + GRID_TOTAL_COLS, orderedTableIds.length - 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          nextIndex = Math.max(currentIndex - GRID_TOTAL_COLS, 0);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          onSelectTable(tableId);
          return;
        default:
          return;
      }

      if (nextIndex >= 0 && nextIndex < orderedTableIds.length) {
        const nextId = orderedTableIds[nextIndex];
        setFocusedTableId(nextId);
        const el = tableRefs.current.get(nextId);
        if (el) el.focus();
      }
    },
    [orderedTableIds, onSelectTable, GRID_TOTAL_COLS],
  );

  // VIP detection from cached seats
  const vipTableIds = useMemo(() => {
    const ids = new Set<string>();
    seatCacheRef.current.forEach((seats, tableId) => {
      if (seats.some((s) => s.protocolVip)) {
        ids.add(tableId);
      }
    });
    return ids;
  }, [tables, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: TEXT_MUTED, fontSize: '0.9rem' }}>
        Loading floorplan...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          color: '#b71c1c',
          backgroundColor: '#fdecea',
          padding: '0.75rem 1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          fontSize: '0.85rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        role="alert"
      >
        <span>{error}</span>
        <button
          style={{
            padding: '0.35rem 0.75rem',
            backgroundColor: '#c62828',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '0.8rem',
            cursor: 'pointer',
          }}
          onClick={fetchData}
        >
          Retry
        </button>
      </div>
    );
  }

  if (tables.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: TEXT_MUTED, fontSize: '0.9rem' }}>
        No tables configured yet.
      </div>
    );
  }

  const hasSearch = searchQuery.trim().length > 0;

  // Venue fixture positions
  const danceFloorCenterY = GRID_OFFSET_Y + TABLE_ROWS * TABLE_SPACING_Y + 80;
  const centerX = GRID_OFFSET_X + LEFT_COLS * TABLE_SPACING_X + WALKWAY_WIDTH / 2;
  const stageY = danceFloorCenterY + 60;
  const avAreaX = GRID_OFFSET_X - 20;
  const avAreaY = GRID_OFFSET_Y - 60;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Responsive banner */}
      {isSmallViewport && (
        <div
          style={{
            backgroundColor: '#fff3e0',
            border: `1px solid ${BORDER_LIGHT}`,
            borderRadius: '4px',
            padding: '0.5rem 0.75rem',
            marginBottom: '0.5rem',
            fontSize: '0.75rem',
            color: TEXT_DARK,
          }}
        >
          For the best experience, use landscape orientation or a desktop device.
        </div>
      )}

      {/* Search */}
      <FloorplanSearch
        query={searchQuery}
        onChange={setSearchQuery}
        resultCount={searchResults.matchedTableIds.size}
      />

      {/* Canvas container */}
      <div
        style={{
          position: 'relative',
          height: '600px',
          border: `1px solid ${BORDER_LIGHT}`,
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#f8f9f8',
        }}
      >
        <ZoomPanCanvas
          scale={scale}
          translateX={translateX}
          translateY={translateY}
          onZoom={handleZoom}
          onPan={handlePan}
        >
          <svg
            width={totalWidth}
            height={totalHeight}
            style={{ display: 'block' }}
          >

            {/* SVG Filters */}
            <defs>
              <filter id="table-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
              <filter id="seat-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

            {/* ===== VENUE PERIMETER ===== */}
            <rect
              x={70}
              y={80}
              width={totalWidth - 140}
              height={totalHeight - 100}
              rx={12}
              ry={12}
              fill="none"
              stroke="#c8d0ca"
              strokeWidth={2}
              strokeDasharray="8 4"
            />

            {/* Ballroom title */}
            <text
              x={totalWidth / 2}
              y={105}
              textAnchor="middle"
              fontSize="16"
              fontWeight="700"
              fill={TEXT_DARK}
              fontFamily={FONT_HEADING}
              opacity={0.6}
            >
              Grand Ballroom
            </text>

            {/* ===== SALON LABELS (perimeter) ===== */}
            {SALON_LABELS.map((sl) => (
              <text
                key={sl.label}
                x={sl.x}
                y={sl.y}
                textAnchor="middle"
                fontSize="11"
                fontWeight="600"
                fill="#8b9a90"
                fontFamily={FONT_HEADING}
                transform={sl.rotate !== 0 ? `rotate(${sl.rotate}, ${sl.x}, ${sl.y})` : undefined}
              >
                {sl.label}
              </text>
            ))}

            {/* ===== AV TABLES (top-left) ===== */}
            <g>
              <rect
                x={avAreaX}
                y={avAreaY}
                width={120}
                height={40}
                rx={6}
                fill="#e8eae9"
                stroke="#b0b8b3"
                strokeWidth={1}
              />
              <text
                x={avAreaX + 60}
                y={avAreaY + 24}
                textAnchor="middle"
                fontSize="10"
                fontWeight="600"
                fill="#5c6b63"
              >
                🎛️ AV / Sound
              </text>
            </g>

            {/* ===== MAIN WALKWAY (Grand Entrance → Dance Floor) ===== */}
            <rect
              x={centerX - 30}
              y={GRID_OFFSET_Y - 30}
              width={60}
              height={TABLE_ROWS * TABLE_SPACING_Y + 40}
              rx={4}
              fill="#f0f2f1"
              stroke="#d4dbd6"
              strokeWidth={1}
              strokeDasharray="6 4"
            />
            <text
              x={centerX}
              y={GRID_OFFSET_Y - 38}
              textAnchor="middle"
              fontSize="9"
              fill="#8b9a90"
              fontWeight="600"
            >
              MAIN WALKWAY
            </text>

            {/* Entrance arrow indicator */}
            <polygon
              points={`${centerX},${GRID_OFFSET_Y - 55} ${centerX - 8},${GRID_OFFSET_Y - 45} ${centerX + 8},${GRID_OFFSET_Y - 45}`}
              fill="#8b9a90"
            />

            {/* ===== DANCE FLOOR (enveloped by stage) ===== */}
            {/* Stage wraps around dance floor */}
            <rect
              x={centerX - 220}
              y={stageY - 10}
              width={440}
              height={120}
              rx={10}
              fill="#2d2d2d"
              stroke="#1a1a1a"
              strokeWidth={2}
            />
            {/* Stage label */}
            <text
              x={centerX}
              y={stageY + 100}
              textAnchor="middle"
              fontSize="12"
              fontWeight="700"
              fill="#ffffff"
              fontFamily={FONT_HEADING}
            >
              🎵 STAGE
            </text>

            {/* Dance floor (inside stage) */}
            <ellipse
              cx={centerX}
              cy={danceFloorCenterY}
              rx={120}
              ry={55}
              fill="#3a3a3a"
              stroke="#555555"
              strokeWidth={1.5}
            />
            <text
              x={centerX}
              y={danceFloorCenterY - 8}
              textAnchor="middle"
              fontSize="13"
              fontWeight="700"
              fill="#ffffff"
              fontFamily={FONT_HEADING}
            >
              Dance Floor
            </text>
            <text
              x={centerX}
              y={danceFloorCenterY + 14}
              textAnchor="middle"
              fontSize="9"
              fill="#cccccc"
            >
              DJ Booth
            </text>

            {/* ===== ZONE LABELS ===== */}
            {zoneLabels.map((zl) => (
              <text
                key={zl.name}
                x={GRID_OFFSET_X - 60}
                y={zl.y + 5}
                fontSize="10"
                fontWeight="700"
                fill={zl.color}
                fontFamily={FONT_HEADING}
                opacity={0.8}
              >
                {zl.name}
              </text>
            ))}

            {/* ===== TABLE CIRCLES ===== */}
            {tables.map((table) => {
              const pos = positions.get(table.id);
              if (!pos) return null;

              const isHighlighted = hasSearch && searchResults.matchedTableIds.has(table.id);
              const isDimmed = hasSearch && !searchResults.matchedTableIds.has(table.id);
              const cachedSeats = seatCacheRef.current.get(table.id) || [];
              const highlightedSeatIds = Array.from(searchResults.matchedSeatIds).filter((seatId) =>
                cachedSeats.some((s) => s.id === seatId),
              );

              return (
                <g
                  key={table.id}
                  transform={`translate(${pos.x}, ${pos.y})`}
                  opacity={isDimmed ? 0.4 : 1}
                  ref={(el) => {
                    if (el) tableRefs.current.set(table.id, el);
                  }}
                >
                  <TableCircle
                    table={table}
                    seats={cachedSeats}
                    isHighlighted={isHighlighted}
                    highlightedSeatIds={highlightedSeatIds}
                    hasVip={vipTableIds.has(table.id)}
                    onClick={() => onSelectTable(table.id)}
                    onMouseEnter={(e) => handleTableMouseEnter(table, e)}
                    onMouseLeave={handleTableMouseLeave}
                    onFocus={() => setFocusedTableId(table.id)}
                    onKeyDown={(e) => handleTableKeyDown(table.id, e)}
                  />
                </g>
              );
            })}
          </svg>
        </ZoomPanCanvas>

        {/* Zoom controls */}
        <ZoomControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onResetFit={handleResetFit}
          currentScale={scale}
        />
      </div>

      {/* Tooltip */}
      <FloorplanTooltip
        table={tooltipTable}
        seats={tooltipSeats}
        position={tooltipPosition}
        visible={tooltipVisible}
      />
    </div>
  );
}
